import { Component, Input, OnChanges } from '@angular/core';
import { ElementBaseComponent } from '../elements-base/element-base.component';
import { FLEX_LAYOUT_ENUM } from '../../enum/layout.enum';

@Component({
  selector: 'clients-grid-layout',
  template: `
    <div [ngClass]="mergedClasses" [ngStyle]="mergedStyles">
      <ng-content></ng-content>
    </div>
  `,
})
export class LayoutComponent extends ElementBaseComponent implements OnChanges {
  @Input() layout: FLEX_LAYOUT_ENUM;
  @Input() align?: string;
  @Input() alignXs?: string;
  @Input() alignSm?: string;
  @Input() layoutXs?: FLEX_LAYOUT_ENUM = FLEX_LAYOUT_ENUM.COLUMN;
  @Input() layoutSm?: FLEX_LAYOUT_ENUM = FLEX_LAYOUT_ENUM.COLUMN;
  @Input() layoutMd?: FLEX_LAYOUT_ENUM = FLEX_LAYOUT_ENUM.ROW;
  @Input() classTailwind: string | string[] = [];
  @Input() layoutGap: string = '20px';
  @Input() customStyles?: string | { [key: string]: string };

  @Input() xsWidth = 100;
  @Input() smWidth = 100;
  @Input() mdWidth = 100;
  @Input() lgWidth = 100;
  @Input() xlgWidth = 100;

  @Input() hideXs = false;
  @Input() hideSm = false;
  @Input() hideMd = false;
  @Input() hideLg = false;

  public mergedClasses: string[] = [];

  ngOnChanges(): void {
    const tailwindClasses = Array.isArray(this.classTailwind)
      ? this.classTailwind
      : (this.classTailwind || '').split(' ');

    this.mergedClasses = [...this.buildClassList(), ...tailwindClasses].filter(Boolean);
  }

  get mergedStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    // Apply layout gap
    if (this.layoutGap) {
      styles['gap'] = this.layoutGap;
    }

    // Merge custom styles (string or object)
    if (typeof this.customStyles === 'string') {
      this.customStyles.split(';').forEach((style) => {
        const [key, value] = style.split(':').map((s) => s.trim());
        if (key && value) {
          styles[key] = value;
        }
      });
    } else if (typeof this.customStyles === 'object' && this.customStyles !== null) {
      Object.assign(styles, this.customStyles);
    }

    return styles;
  }

  private buildClassList(): string[] {
    const classes: string[] = ['fb-auto', 'flex', 'w-full', 'h-full', 'basis-auto!'];

    // Layout
    if (this.layout) {
      classes.push(this.layout === FLEX_LAYOUT_ENUM.ROW ? 'flex-row' : 'flex-col');
    }

    // Widths
    classes.push(`fxs:w-[${this.xsWidth}%]`);
    classes.push(`fsm:w-[${this.smWidth}%]`);
    classes.push(`fmd:w-[${this.mdWidth}%]`);
    classes.push(`flg:w-[${this.lgWidth}%]`);
    classes.push(`fxl:w-[${this.xlgWidth}%]`);

    // Hide conditions
    if (this.hideXs) classes.push('fxs:hidden');
    if (this.hideSm) classes.push('fsm:hidden');
    if (this.hideMd) classes.push('fmd:hidden');
    if (this.hideLg) classes.push('flg:hidden');

    // Layout breakpoints
    classes.push(...this.mapLayoutEnumToTailwind(this.layout));
    if (this.layoutXs) classes.push(...this.mapLayoutEnumToTailwind(this.layoutXs, 'fxs'));
    if (this.layoutSm) classes.push(...this.mapLayoutEnumToTailwind(this.layoutSm, 'fsm'));
    if (this.layoutMd) classes.push(...this.mapLayoutEnumToTailwind(this.layoutMd, 'fmd'));

    // Alignments (fixed breakpoint assignments)
    if (this.align) classes.push(this.convertAlign(this.align));
    if (this.alignXs) classes.push(this.convertAlign(this.alignXs, 'fxs'));
    if (this.alignSm) classes.push(this.convertAlign(this.alignSm, 'fsm'));

    return classes;
  }

  private readonly layoutEnumMap: Record<FLEX_LAYOUT_ENUM, string[]> = {
    [FLEX_LAYOUT_ENUM.ROW]: ['flex-row'],
    [FLEX_LAYOUT_ENUM.ROW_WRAP]: ['flex-row', 'flex-wrap'],
    [FLEX_LAYOUT_ENUM.COLUMN]: ['flex-col'],
    [FLEX_LAYOUT_ENUM.COLUMN_REVERSE]: ['flex-col-reverse'],
  };

  private readonly justifyMap: Record<string, string> = {
    start: 'justify-start',
    'flex-start': 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    'flex-end': 'justify-end',
    'space-between': 'justify-between',
    'space-around': 'justify-around',
    'space-evenly': 'justify-evenly',
  };

  private readonly alignItemsMap: Record<string, string> = {
    start: 'items-start',
    'flex-start': 'items-start',
    center: 'items-center',
    end: 'items-end',
    'flex-end': 'items-end',
    stretch: 'items-stretch',
    baseline: 'items-baseline',
  };

  private mapLayoutEnumToTailwind(layout: FLEX_LAYOUT_ENUM, breakpoint: string = ''): string[] {
    const layoutClasses = this.layoutEnumMap[layout] ?? ['flex-row'];
    return breakpoint
      ? layoutClasses.map((item) => `${breakpoint}:${item}`)
      : layoutClasses;
  }

  private mapJustifyToTailwind(justify: string): string {
    return this.justifyMap[justify] ?? 'justify-between';
  }

  private mapAlignItemsToTailwind(items: string): string {
    return this.alignItemsMap[items] ?? 'items-stretch';
  }

  private convertAlign(align: string, breakpoint: string = ''): string {
    const [justify, items] = (align || '').split(' ');
    const classes: string[] = [];

    if (justify) classes.push(this.mapJustifyToTailwind(justify));
    if (items) classes.push(this.mapAlignItemsToTailwind(items));
    return breakpoint
      ? classes.map((cls) => `${breakpoint}:${cls}`).join(' ')
      : classes.join(' ');
  }
}
